/**
 * A tab's copy of its workspace's communication report counters (Phase 5 of
 * docs/candidate-communications-experience.md), used by the client App. One load at a time; live updates
 * (`COMM_METRICS_UPDATED`) are added once each (`addCommMetricsUpdate`), and those that come while it loads are kept
 * for it. A reset (sign-out, reload) drops the counters, and a load under way then answers nothing.
 */
import { addCommMetricsUpdate } from './commEvents';
export class CommMetricsCache {
    constructor(fetch, 
    /** The counters changed: loaded, updated, or (null) dropped by a reset that tells views to load them again. */
    changed) {
        this.fetch = fetch;
        this.changed = changed;
        this.snapshot = null;
        this.loading = null;
        /** Updates that came while loading: added once the load is in (those it already has are skipped). */
        this.pending = [];
        /** The update entries added to the loaded counters, so none counts twice. */
        this.counted = new Set();
    }
    /** The counters, or null until they're loaded. */
    get() {
        return this.snapshot;
    }
    /** Load the counters (joining a load under way). Rejects when the load fails. */
    load() {
        if (this.loading)
            return this.loading;
        this.pending = [];
        const request = this.fetch()
            .then((loaded) => {
            // Reset meanwhile (signed out, or reloaded): a newer load, if any, answers for this one.
            if (this.loading !== request)
                return this.loading ?? undefined;
            const pending = this.pending;
            this.pending = [];
            this.snapshot = loaded;
            this.counted = new Set();
            for (const update of pending)
                this.apply(update);
            this.changed(this.snapshot);
            return undefined;
        })
            .finally(() => {
            if (this.loading === request)
                this.loading = null;
        });
        this.loading = request;
        return request;
    }
    /** A live update: added once, or kept for the load under way; ignored when the counters aren't loaded. */
    add(update) {
        if (this.snapshot === null) {
            if (this.loading)
                this.pending.push(update);
            return;
        }
        if (this.apply(update))
            this.changed(this.snapshot);
    }
    /**
     * Drop the counters and any load under way. `tellViews`: if there were any (or a load), say so (`changed(null)`),
     * so views showing them load them again.
     */
    reset(tellViews) {
        const shown = this.snapshot !== null || this.loading !== null;
        this.snapshot = null;
        this.loading = null;
        this.pending = [];
        this.counted = new Set();
        if (tellViews && shown)
            this.changed(null);
    }
    /** Whether the update added anything. */
    apply(update) {
        if (this.snapshot === null)
            return false;
        const result = addCommMetricsUpdate(this.snapshot, update, this.counted);
        if (result === null)
            return false;
        this.snapshot = result.snapshot;
        for (const key of result.added)
            this.counted.add(key);
        return true;
    }
}
